import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const EmailVerification = ({ 
  open, 
  email, 
  firstName,
  onVerified, 
  onClose 
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  
  const inputRefs = useRef([]);
  useEffect(() => {
    if (open && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendCountdown]);

  const sendOTP = async () => {
    setResendLoading(true);
    setError("");
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/otp/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier: email,first_name: firstName }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send OTP");
      }
      setResendDisabled(true);
      setResendCountdown(60);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      setError(err.message || "Failed to send verification code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (newOtp.every(digit => digit !== "") && index === 5) {
      verifyOTP(newOtp.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtp(digits);
      setError("");
      inputRefs.current[5]?.focus();
      verifyOTP(pastedData);
    }
  };

  const verifyOTP = async (otpString = null) => {
    const otpToVerify = otpString || otp.join("");
    
    if (otpToVerify.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/otp/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            identifier: email, 
            otp: otpToVerify 
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setAttemptsLeft(prev => Math.max(0, prev - 1));
        if (attemptsLeft <= 1) {
          throw new Error("Too many incorrect attempts. Please request a new code.");
        }
        throw new Error(data.message || "Invalid verification code");
      }
      onVerified();
      
    } catch (err) {
      console.error("Verify OTP error:", err);
      setError(err.message || "Verification failed. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      if (attemptsLeft <= 1) {
        setAttemptsLeft(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (resendDisabled || resendLoading) return;
    
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setAttemptsLeft(5); 
    sendOTP();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "#000000",
          border: "2px solid #3b82f6",
          borderRadius: "12px",
          color: "white",
          width: "500px",
          maxWidth: "90vw",
          minHeight: "auto",
          position: "relative",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#000000",
          color: "white",
          textAlign: "center",
          fontSize: "1.5rem",
          fontWeight: "bold",
          paddingBottom: "10px",
          marginBottom: "10px",
          position: "relative",
        }}
      >
        Verify Your Email
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#3b82f6",
            "&:hover": {
              backgroundColor: "rgba(59, 130, 246, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: "#000000",
          color: "white",
          textAlign: "center",
          padding: "20px 30px",
        }}
      >
        <p style={{ color: "#94a3b8", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
          We've sent a 6-digit verification code to
        </p>
        <p style={{ color: "#ffffff", marginBottom: "2rem", fontWeight: "500" }}>
          {email}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginBottom: "1.5rem",
          }}
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={loading || attemptsLeft === 0}
              style={{
                width: "50px",
                height: "60px",
                fontSize: "1.5rem",
                textAlign: "center",
                backgroundColor: "#1a1a1a",
                border: `2px solid ${digit ? "#3b82f6" : "#374151"}`,
                borderRadius: "8px",
                color: "#ffffff",
                outline: "none",
                transition: "all 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
              }}
              onBlur={(e) => {
                if (!digit) e.target.style.borderColor = "#374151";
              }}
            />
          ))}
        </div>
        {error && (
          <p
            style={{
              color: "#ef4444",
              fontSize: "0.875rem",
              marginBottom: "1rem",
            }}
          >
            {error}
          </p>
        )}
        {attemptsLeft < 5 && attemptsLeft > 0 && (
          <p style={{ color: "#fbbf24", fontSize: "0.875rem", marginBottom: "1rem" }}>
            {attemptsLeft} attempt{attemptsLeft !== 1 ? "s" : ""} remaining
          </p>
        )}
        <div style={{ marginTop: "1.5rem" }}>
          {resendCountdown > 0 ? (
            <p style={{ color: "#94a3b8", fontSize: "0.875rem" }}>
              Didn't receive the code? Resend in {resendCountdown}s
            </p>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
              <p style={{ color: "#94a3b8", fontSize: "0.875rem", margin: 0 }}>
                Didn't receive the code?
              </p>
              <button
                onClick={handleResend}
                disabled={resendDisabled || resendLoading}
                style={{
                  background: "transparent",
                  border: "none",
                  color: resendDisabled || resendLoading ? "#4b5563" : "#3b82f6",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  cursor: resendDisabled || resendLoading ? "not-allowed" : "pointer",
                  textDecoration: "underline",
                  padding: "0",
                  transition: "color 0.2s",
                }}
                onMouseOver={(e) => {
                  if (!resendDisabled && !resendLoading) {
                    e.target.style.color = "#2563eb";
                  }
                }}
                onMouseOut={(e) => {
                  if (!resendDisabled && !resendLoading) {
                    e.target.style.color = "#3b82f6";
                  }
                }}
              >
                {resendLoading ? "Sending..." : "Resend Code"}
              </button>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: "#000000",
          justifyContent: "center",
          paddingBottom: "20px",
          gap: "12px",
        }}
      >
        <Button
          variant="contained"
          onClick={() => verifyOTP()}
          disabled={loading || otp.some(d => !d) || attemptsLeft === 0}
          sx={{
            backgroundColor: "#3b82f6",
            color: "white",
            padding: "10px 40px",
            fontSize: "1rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#2563eb",
            },
            "&:disabled": {
              backgroundColor: "#374151",
              color: "#6b7280",
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ color: "white", marginRight: "8px" }} />
              Verifying...
            </>
          ) : (
            "Verify Email"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerification;